<script lang="ts" setup>
import Autocomplete from 'primevue/autocomplete'
import type { CustomerDbRecord } from '~/types/db/customer'

const props = withDefaults(defineProps<{
  modelValue?: CustomerDbRecord | null
  showForm?: boolean
}>(), {
  modelValue: null,
  showForm: true,
})

const emit = defineEmits<{
  'update:modelValue': [customer: CustomerDbRecord | null]
  'select': [customer: CustomerDbRecord]
  'clear': []
  'created': [customer: CustomerDbRecord]
}>()

const { t } = useNuxtApp().$i18n
const businessStore = useBusinessStore()
const { businessId } = storeToRefs(businessStore)

// Search state
const searchQuery = ref('')
const suggestions = ref<CustomerDbRecord[]>([])

// New customer form
const showNewForm = ref(false)
const newName = ref('')
const newPhone = ref('')
const newEmail = ref('')
const newAddress = ref('')
const newComments = ref('')
const saving = ref(false)

// Form fields (editable after selection or manual entry)
const customerName = ref(props.modelValue?.name || '')
const customerPhone = ref(props.modelValue?.phone || '')
const customerEmail = ref(props.modelValue?.email || '')
const customerAddress = ref(props.modelValue?.address_line_1 || '')
const customerComments = ref(props.modelValue?.comments || '')
const selectedCustomer = ref<CustomerDbRecord | null>(props.modelValue || null)

// Sync from parent
watch(() => props.modelValue, (val) => {
  if (val) {
    selectedCustomer.value = val
    customerName.value = val.name || ''
    customerPhone.value = val.phone || ''
    customerEmail.value = val.email || ''
    customerAddress.value = val.address_line_1 || ''
    customerComments.value = val.comments || ''
    searchQuery.value = val.name || ''
  }
  else {
    selectedCustomer.value = null
    customerName.value = ''
    customerPhone.value = ''
    customerEmail.value = ''
    customerAddress.value = ''
    customerComments.value = ''
    searchQuery.value = ''
    showNewForm.value = false
  }
})

async function search(event: { query: string }) {
  try {
    const res = await $fetch(`/api/business/${businessId.value}/customers/search`, {
      method: 'GET',
      params: { needle: event.query },
    }) as { data: CustomerDbRecord[] }
    suggestions.value = res.data || []
  }
  catch {
    suggestions.value = []
  }
}

function onSelect(event: { value: CustomerDbRecord }) {
  const customer = event.value
  selectedCustomer.value = customer
  customerName.value = customer.name
  customerPhone.value = customer.phone || ''
  customerEmail.value = customer.email || ''
  customerAddress.value = customer.address_line_1 || ''
  customerComments.value = customer.comments || ''
  emit('update:modelValue', customer)
  emit('select', customer)
}

function clearSelection() {
  selectedCustomer.value = null
  customerName.value = ''
  customerPhone.value = ''
  customerEmail.value = ''
  customerAddress.value = ''
  customerComments.value = ''
  searchQuery.value = ''
  emit('update:modelValue', null)
  emit('clear')
}

async function saveNewCustomer() {
  if (!newName.value.trim()) return

  saving.value = true
  try {
    const res = await $fetch(`/api/business/${businessId.value}/customers`, {
      method: 'POST',
      body: {
        name: newName.value,
        phone: newPhone.value || undefined,
        email: newEmail.value || undefined,
        address_line_1: newAddress.value || undefined,
        comments: newComments.value || undefined,
      },
    }) as { data: CustomerDbRecord }

    if (res.data) {
      selectedCustomer.value = res.data
      customerName.value = res.data.name
      customerPhone.value = res.data.phone || ''
      customerEmail.value = res.data.email || ''
      customerAddress.value = res.data.address_line_1 || ''
      customerComments.value = res.data.comments || ''
      searchQuery.value = res.data.name
      showNewForm.value = false
      newName.value = ''
      newPhone.value = ''
      newEmail.value = ''
      newAddress.value = ''
      newComments.value = ''
      useSuccessToast('Customer created successfully')
      emit('update:modelValue', res.data)
      emit('select', res.data)
      emit('created', res.data)
    }
  }
  catch {
    useErrorToast('Failed to create customer')
  }
  finally {
    saving.value = false
  }
}

async function updateCustomer() {
  if (!selectedCustomer.value?.id) return

  saving.value = true
  try {
    const res = await $fetch(`/api/business/${businessId.value}/customers/${selectedCustomer.value.id}`, {
      method: 'PUT',
      body: {
        name: customerName.value,
        phone: customerPhone.value || undefined,
        email: customerEmail.value || undefined,
        address_line_1: customerAddress.value || undefined,
        comments: customerComments.value || undefined,
      },
    }) as { data: CustomerDbRecord }

    if (res.data) {
      selectedCustomer.value = res.data
      useSuccessToast('Customer updated successfully')
      emit('update:modelValue', res.data)
    }
  }
  catch {
    useErrorToast('Failed to update customer')
  }
  finally {
    saving.value = false
  }
}

// Expose for parent components that need raw values
defineExpose({
  selectedCustomer,
  customerName,
  customerPhone,
  customerEmail,
  customerAddress,
  customerComments,
})
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Search row -->
    <div
      v-if="!showNewForm"
      class="flex flex-col gap-1"
    >
      <label class="text-sm font-medium text-gray-700">{{ t('Client') }}</label>
      <div class="flex gap-2">
        <Autocomplete
          v-model="searchQuery"
          :delay="400"
          :suggestions="suggestions"
          option-label="name"
          :placeholder="t('Search by name / phone / email')"
          class="flex-1"
          fluid
          @complete="search"
          @item-select="onSelect"
        >
          <template #option="slotProps">
            <div class="flex gap-1 text-sm">
              <span class="font-medium">{{ slotProps.option.name }}</span>
              <span
                v-if="slotProps.option.phone"
                class="text-gray-500"
              >· {{ slotProps.option.phone }}</span>
              <span
                v-if="slotProps.option.email"
                class="text-gray-500"
              >· {{ slotProps.option.email }}</span>
            </div>
          </template>
        </Autocomplete>
        <Button
          icon="pi pi-user-plus"
          severity="success"
          :aria-label="t('Add Client')"
          @click="showNewForm = true"
        />
        <Button
          v-if="selectedCustomer"
          icon="pi pi-times"
          severity="secondary"
          text
          :aria-label="t('Clear client')"
          @click="clearSelection"
        />
      </div>
    </div>

    <!-- New customer form -->
    <div
      v-if="showNewForm"
      class="flex flex-col gap-3 p-3 bg-gray-50 rounded-lg"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm font-semibold text-gray-700">{{ t('Add Client') }}</span>
        <Button
          icon="pi pi-times"
          severity="secondary"
          text
          size="small"
          @click="showNewForm = false"
        />
      </div>
      <InputText
        v-model="newName"
        :placeholder="t('Name')"
        class="w-full"
      />
      <InputText
        v-model="newPhone"
        :placeholder="t('Phone')"
        class="w-full"
      />
      <InputText
        v-model="newEmail"
        :placeholder="t('Email')"
        class="w-full"
      />
      <InputText
        v-model="newAddress"
        :placeholder="t('Address 1')"
        class="w-full"
      />
      <Textarea
        v-model="newComments"
        :placeholder="t('Comments')"
        class="w-full"
        rows="2"
        auto-resize
      />
      <Button
        :label="t('Save')"
        icon="pi pi-check"
        severity="success"
        size="small"
        :loading="saving"
        :disabled="!newName.trim()"
        @click="saveNewCustomer"
      />
    </div>

    <!-- Selected customer details (editable) -->
    <div
      v-if="showForm && selectedCustomer"
      class="flex flex-col gap-2"
    >
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">{{ t('Name') }}</label>
        <InputText
          v-model="customerName"
          :placeholder="t('Name')"
          class="w-full"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">{{ t('Phone') }}</label>
        <InputText
          v-model="customerPhone"
          :placeholder="t('Phone')"
          class="w-full"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">{{ t('Email') }}</label>
        <InputText
          v-model="customerEmail"
          :placeholder="t('Email')"
          class="w-full"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">{{ t('Address 1') }}</label>
        <InputText
          v-model="customerAddress"
          :placeholder="t('Address 1')"
          class="w-full"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">{{ t('Comments') }}</label>
        <Textarea
          v-model="customerComments"
          :placeholder="t('Comments')"
          class="w-full"
          rows="2"
          auto-resize
        />
      </div>
      <Button
        :label="t('Update customer info')"
        icon="pi pi-save"
        severity="secondary"
        size="small"
        :loading="saving"
        @click="updateCustomer"
      />
    </div>
  </div>
</template>
